import type { Props as ButtonProps } from '@studio/design-system/button';
import { Button as _Button } from '@studio/design-system/button';
import { Busy } from '@studio/design-system/icon';
import type { HtmlAttributes, HtmlAttributesWithChildren } from '@studio/design-system/props';
import type { T_Optional } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useContext } from 'react';
import { createPortal } from 'react-dom';
import { isElement } from 'react-is';
import { twMerge } from 'cn';
import type { VerbiageProps } from './types.js';
import { WizardActionsContext } from './wizard.jsx';

/** `<Button>` component. */
export const Button = ({
  busy,
  children,
  className,
  size = 'xl',
  ...props
}: ButtonProps & T_Optional<{ busy: boolean }>): ReactElement => (
  <_Button
    className={twMerge('h-auto min-h-[56px] max-w-full whitespace-normal px-24 py-12', className)}
    size={size}
    {...props}
  >
    {!!busy && <Busy className='mr-8' />}
    {children}
  </_Button>
);

/** `<ButtonBox>` component. */
export const ButtonBox = ({
  children,
  className,
  ...props
}: HtmlAttributesWithChildren): ReactElement | null => {
  const { el, inWizard } = useContext(WizardActionsContext);
  const node = (
    <div
      className={twMerge(
        'flex max-w-full flex-col items-center gap-16 [&_button]:max-w-full [&_button]:whitespace-normal',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );

  if (el) {
    return createPortal(node, el);
  }
  return inWizard ? null : node;
};

/** `<Separator>` component. */
export const Separator = ({ className, ...props }: HtmlAttributes): ReactElement => (
  <hr
    className={twMerge(
      'w-full border-white [@media(min-width:480px)_and_(min-height:720px)]:mb-6 [@media(min-width:480px)_and_(min-height:720px)]:mt-12',
      className
    )}
    {...props}
  />
);

/** `<Verbiage>` component. */
export const Verbiage = ({ message, title, ...props }: VerbiageProps): ReactElement => (
  <div {...props}>
    {isElement(title) ? title : <Verbiage.Title>{title}</Verbiage.Title>}
    {isElement(message) ? message : <Verbiage.Message>{message}</Verbiage.Message>}
  </div>
);

Verbiage.Title = function Title({
  children,
  className,
  ...props
}: HtmlAttributesWithChildren): ReactElement {
  return (
    <h1 className={twMerge('mb-10', className)} {...props}>
      {children}
    </h1>
  );
};

Verbiage.Message = function Message({
  children,
  className,
  ...props
}: HtmlAttributesWithChildren): ReactElement {
  return (
    <p className={twMerge('leading-150 text-base-50', className)} {...props}>
      {children}
    </p>
  );
};
